import { useMsal } from '@azure/msal-react';
import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box, Stack } from '@mui/system';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CollapseTreeFortLogo from '@/assets/icons/collapse-treefort.png';
import LexoSideNavCollapsedLogo from '@/assets/icons/lexo-sidenav-collapsed-logo.svg';
import LexoSideNavLogo from '@/assets/icons/lexo-sidenav-logo.svg';
import SideNavToggleIcon from '@/assets/icons/sidenav-toggle-icon.svg';
import TreeFortLogo from '@/assets/icons/treefort.png';
import LawFirmSwitchDialog from '@/components/LawFirmSwitchDialog';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { getDownloadLink } from '@/services/FileService';
import useIdentityStore from '@/stores/useIdentityStore';
import { SIDE_NAV_CUSTOM_ICON } from './SideNavIcon';
import SideNavItem from './SideNavItem';
import SideNavLawFirm from './SideNavLawFirm';
import type { SideNavItemType } from './types';

export default function SideNav() {
    const theme = useTheme();
    const location = useLocation();
    const { instance } = useMsal();
    const navigate = useNavigate();
    const loggedInUser = useIdentityStore((state) => state.user);
    const { isSuperAdmin, activeLawFirmId } = useLawFirmContext();
    const { data: activeLawFirm } = useLawFirm(isSuperAdmin ? activeLawFirmId : undefined);

    const displayLawFirm = isSuperAdmin && activeLawFirm ? activeLawFirm : loggedInUser?.lawFirm;
    const lawFirmName = displayLawFirm?.companyName;
    const lawFirmLogoSrc = displayLawFirm?.logoUrl ? getDownloadLink(displayLawFirm.logoUrl) : '';
    const userName = loggedInUser?.fullName ?? 'User';

    const [isLawFirmDialogOpen, setIsLawFirmDialogOpen] = useState(false);

    const [activeItem, setActiveItem] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);
    const items = useMemo<SideNavItemType[]>(
        () => [
            ...(isSuperAdmin
                ? [
                      {
                          label: 'Dashboard',
                          icon: 'ph:chart-bar' as const,
                          link: '/dashboard',
                      },
                  ]
                : []),
            {
                label: 'Prospects',
                icon: 'ph:handshake',
                link: '/prospects',
            },
            {
                label: 'ID Verification',
                icon: SIDE_NAV_CUSTOM_ICON,
                link: '/idv',
            },
            {
                label: 'Templates',
                icon: 'ph:puzzle-piece',
                link: '/templates/intakes',
            },
            ...(isSuperAdmin
                ? [
                      {
                          label: 'Law Firms',
                          icon: 'ph:building-apartment' as const,
                          link: '/law-firms',
                      },
                  ]
                : []),
            {
                label: 'Users',
                icon: 'ph:users-three',
                link: '/users',
            },
            {
                label: 'Contacts',
                icon: 'ph:address-book',
                link: '/contacts',
            },
            {
                label: 'Billing',
                icon: 'stash:billing-info',
                link: '/billing',
            },
            {
                label: 'Settings',
                icon: 'ph:gear-six',
                link: '/settings/logo',
            },
        ],
        [isSuperAdmin],
    );

    const [isUserManagementExpanded, setIsUserManagementExpanded] = useState(false);

    useEffect(() => {
        const activeRoute: string = location.pathname;
        const matchedLinkItem = items.find((item) => {
            if (!item.link) return false;
            if (item.link === activeRoute) return true;
            if (item.label === 'Prospects' && (activeRoute === '/' || activeRoute === '/prospects'))
                return true;
            if (item.label === 'Templates' && activeRoute.startsWith('/templates')) return true;
            if (item.label === 'Contacts' && activeRoute.startsWith('/contacts')) return true;
            return false;
        });
        if (matchedLinkItem) {
            setActiveItem(matchedLinkItem.label ?? '');
        }
    }, [location.pathname, items]);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    position: 'relative',
                    width: isExpanded ? '200px' : '48px',
                    height: '100vh',
                    backgroundColor: theme.palette.sidenav.main,
                    transition: 'width 0.2s ease-in',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Box
                        onClick={() => navigate('/')}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            paddingTop: '24px',
                            paddingBottom: '24px',
                            cursor: 'pointer',
                        }}
                    >
                        <img src={isExpanded ? LexoSideNavLogo : LexoSideNavCollapsedLogo} alt="" />
                    </Box>
                    {lawFirmName && (
                        <>
                            <SideNavLawFirm
                                name={lawFirmName}
                                logoSrc={lawFirmLogoSrc || undefined}
                                isExpanded={isExpanded}
                                isInteractive={isSuperAdmin}
                                // Privacy is between the firm and Lexo, so only Lexo Admins see it
                                isPrivate={isSuperAdmin && !!displayLawFirm?.isPrivate}
                                onClick={() => setIsLawFirmDialogOpen(true)}
                            />
                            <Divider
                                sx={{
                                    margin: '4px 16px',
                                    backgroundColor: theme.palette.sidenav.dividerBackground,
                                }}
                            />
                        </>
                    )}
                    <Stack>
                        {items.map((item) => (
                            <SideNavItem
                                key={item.label}
                                item={item}
                                isActive={item.label === activeItem}
                                isExpanded={isExpanded}
                            />
                        ))}
                    </Stack>
                </Box>
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    {isExpanded && (
                        <Typography
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                fontSize: '14px',
                                marginBottom: '-15px',
                                color: theme.palette.sidenav.contrastText,
                            }}
                        >
                            Integrates with
                        </Typography>
                    )}
                    <a
                        href="https://treeforttech.com/2024/07/17/treefort-technologies-partnership-with-lexo/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        <img
                            src={isExpanded ? TreeFortLogo : CollapseTreeFortLogo}
                            alt="Treefort Logo"
                            style={{
                                width: isExpanded ? '150px' : '30px',
                                height: isExpanded ? '80px' : '50px',
                                objectFit: 'contain',
                                cursor: 'pointer',
                            }}
                        />
                    </a>
                    <Divider
                        sx={{
                            margin: '4px 16px',
                            backgroundColor: theme.palette.sidenav.dividerBackground,
                        }}
                    ></Divider>
                    <SideNavItem
                        item={{
                            icon: 'ph:user-circle',
                            label: userName,
                            activeColor: theme.palette.neutral.N_700,
                            hideActiveBorder: true,
                        }}
                        isActive={isUserManagementExpanded}
                        isExpanded={isExpanded}
                        onClick={() => {
                            setIsUserManagementExpanded(!isUserManagementExpanded);
                        }}
                    />
                </Box>
            </Box>
            <img
                style={{
                    position: 'absolute',
                    top: '50%',
                    right: '-16px',
                    transform: `rotate(${isExpanded ? 0 : 180}deg)`,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in',
                }}
                onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
                src={SideNavToggleIcon}
                alt="Side Nav Toggle Icon"
            />
            {isUserManagementExpanded && (
                <Box
                    sx={{
                        backgroundColor: theme.palette.sidenav.main,
                        zIndex: 1000,
                        display: 'flex',
                        width: 'fit-content',
                        position: 'absolute',
                        bottom: '50px',
                        left: '0',
                        borderRadius: '4px',
                    }}
                >
                    <Stack sx={{ backgroundColor: theme.palette.neutral.N_700 }}>
                        <SideNavItem
                            item={{
                                icon: 'ph:sign-out',
                                label: 'Log out',
                            }}
                            isActive={false}
                            isExpanded={isExpanded}
                            onClick={() => instance.logoutRedirect()}
                        />
                    </Stack>
                </Box>
            )}
            {isSuperAdmin && (
                <LawFirmSwitchDialog
                    open={isLawFirmDialogOpen}
                    onClose={() => setIsLawFirmDialogOpen(false)}
                />
            )}
        </Box>
    );
}
